/* global Stripe */
/// <reference types="@types/stripe-v3" />
import { Controller } from '@hotwired/stimulus'
import api from '../../helpers/api'
import { log } from '../../helpers/debug'
import computedValues from '../../helpers/computedValues'
import {
  connectPageRequirements,
  connectUserData,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  getFlow,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import notify from '../../helpers/notify'
import { getRendererBase, STRIPE_TEST_PKEY, TEST_MODE } from '../../helpers/utils'
import jitsu from '../../helpers/jitsu'

import { StripeCheckout, UserData } from '../../api/models/types'
import replaceStorageURLsFromFirebaseToCloudflare from '../../api/helpers/replaceStorageURLsFromFirebaseToCloudflare'

// @TODO: Analytics - more properties

const CARD_ADDED = 'card_added'
const PAID = 'paid'

type FlowContext = {
  flowId: string | null
  component: StripeCheckout
  flowVersion: string | null
  componentId: string
  flowEl: Element | null
  pageEl: Element | null
  pageId: string
}

type UserDataValue = {
  name?: string
  last_name?: string
  first_name?: string
  email?: string
  country?: string
  postal_code?: string
}

type StripeApiData = {
  flow_id: string | null
  component_id: string
  webapp: boolean
  flow_version: string | null
  details: any
  amount: any
  currency: any
  price_id: any
  one_time_price_id: any
  promo_code: any
  trial_period_days: any
}

type OldStripeApiData = {
  formId: string | null
  componentId: string
  webapp: boolean
  flowVersion: string | null
  details?: stripe.BillingDetails
  amount?: number
  currency?: string
  price_id?: string | string[]
  one_time_price_id?: string | string[]
  promo_code?: string | string[]
  trial_period_days?: string
}

export default class extends Controller {
  static classes = ['complete']
  declare completeClass: string

  static targets = [
    'card',
    'cardNum',
    'cardExpiry',
    'cardCvc',
    'wallet',
    'button',
    'errorContainer',
    'successContainer',
    'paymentForm',
  ]
  declare cardTarget: HTMLElement
  declare hasCardTarget: boolean
  declare cardNumTarget: HTMLElement
  declare hasCardNumTarget: boolean
  declare cardExpiryTarget: HTMLElement
  declare hasCardExpiryTarget: boolean
  declare cardCvcTarget: HTMLElement
  declare hasCardCvcTarget: boolean
  declare walletTarget: HTMLElement
  declare hasWalletTarget: boolean
  declare buttonTarget: HTMLElement
  declare hasButtonTarget: boolean
  declare errorContainerTarget: HTMLElement | null
  declare hasErrorContainerTarget: boolean | string
  declare successContainerTarget: HTMLElement
  declare hasSuccessContainerTarget: boolean

  static values = {
    isValid: Boolean,
  }
  declare isValidValue: true

  disconnectPageRequirements?: () => void
  disconnectUserData?: () => void
  userDataValueChanged: (userDataValue?: UserDataValue) => void
  flowContext: FlowContext
  isNotHTTPS: boolean
  collectDetailsOnly?: boolean | string
  declare userDataValue: UserDataValue
  cardElement: stripe.elements.Element
  stripePackage: stripe.Stripe
  clientSecret: string | null
  isSetupIntent: boolean
  localStatus: string
  errorIsBlocking: boolean
  errorMessage: string
  cardExpiryComplete: boolean
  cardCvcComplete: boolean
  cardNumComplete: boolean
  walletElement: stripe.elements.Element
  component: StripeCheckout
  flowEl: Element | null
  pageIndex: number
  componentIndex: number
  pageId: string

  connect() {
    this.disconnectPageRequirements = connectPageRequirements(this)
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    this.flowContext = getComponentContext(this) as unknown as FlowContext
    const isNotHTTPS =
      window.location.protocol !== 'https:' && this.flowContext.component.skip_https_check !== true
    if (isNotHTTPS && this.hasErrorContainerTarget) {
      const notHTTPSWarning = document.createElement('div')
      notHTTPSWarning.innerText = `To make things secure, we only accept payments via HTTPS, not HTTP - please reload this using HTTPS.`
      this.errorContainerTarget?.appendChild(notHTTPSWarning)
      ;(this.errorContainerTarget as HTMLElement).style.display = ''
    }
    this.isNotHTTPS = isNotHTTPS

    /* Static computed variables here */
    this.collectDetailsOnly =
      this.flowContext.component.action === 'collect_details' ||
      this.flowContext.component.collect_details

    this.setUpStripe()
    if ([CARD_ADDED, PAID].includes(this.userDataValue?.[this.flowContext.component?.key])) {
      this.setStatus('success')
    }
    this.component = this.flowContext.component
    this.flowEl = this.flowContext.flowEl
    this.pageId = this.flowContext.pageId

    const flow = getFlow(this, true)
    const pageIndex = flow?.pages.findIndex((page) => page.id === this.flowContext.pageId)
    const componentIndex = flow?.pages[pageIndex]?.components.findIndex(
      (component) => component.id === this.component.id
    )
    this.pageIndex = pageIndex || 0
    this.componentIndex = componentIndex || 0

    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (typeof this.disconnectPageRequirements === 'function') this.disconnectPageRequirements()
  }

  setUpStripe() {
    /* Use Stripe to set up payment UI */
    if (this.flowContext.component.use_new_stripe_elements) {
      this.setUpPaymentElement()
    } else {
      this.setUpCard()
    }
    this.setUpWallet()
  }

  processPayment() {
    log('StripeCheckout: this.getActionStatus()', this.getActionStatus())
    log('StripeCheckout: this.isNotHTTPS', this.isNotHTTPS)
    if (this.getActionStatus()) return
    if (this.isNotHTTPS) return
    this.setStatus('pending')
    this.setErrorMessage('')

    // Check if fields are valid (this.buttonTarget.classList has this.completeClass) - if not then trigger form validation with error message
    const isValid = this.buttonTarget.classList.contains(this.completeClass)
    if (!isValid) {
      this.errorMessage = 'Your card details are incomplete or incorrect'
      this.setStatus('ready')
      this.dispatch('validate')
      // Show error to your customer (e.g., insufficient funds)
      this.dispatch('update', { detail: { key: this.component.key, value: 'failed' } })

      const actions = this.component.on_payment_failed_outputs
      if (Array.isArray(actions))
        this.dispatch('actions', {
          detail: { actions /*, context: { error: result.error }*/, component: this.component },
        })

      this.setErrorMessage(this.errorMessage)
      return
    }

    if (!doNotStoreComponentDataInCloud(this.component)) {
      jitsu.track('payment:attempted', {
        flow_id: this.flowContext.flowId || '',
        payment_service: 'stripe',
      })
    }
    // Payment Began
    const { component } = this.flowContext
    const actions = component.on_payment_attempted_outputs
    if (Array.isArray(actions))
      this.dispatch('actions', {
        detail: { actions, component: this.component },
      })

    window.Savvy.addEventLogs?.([
      {
        flow_id: this.flowContext.flowId || '',
        event_type: 'payment:attempted',
        timestamp: Date.now(),
        user_data_snapshot: this.userDataValue as UserData,
        data: {
          payment_service: 'stripe',
          component_id: this.component.id,
          component_key: this.component.key,
          component_path: [
            ...(this.pageId ? ['pages', `${this.pageIndex}`] : []),
            'components',
            `${this.componentIndex}`,
          ],
        },
      },
    ])
    try {
      if (TEST_MODE && window.location.search.includes('force_stripe_error'))
        throw new Error('An error occurred while processing the payment')
      this.confirmStripeIntent(
        { card: this.cardElement, billing_details: this.computeContactInfo() },
        true
      )
    } catch (error) {
      // if (!this.isInWebApp) Sentry.captureException(error)
      this.setStatus('ready')
      console.error(error)
      this.setErrorMessage(error.message)
      if (!doNotStoreComponentDataInCloud(this.component)) {
        jitsu.track('payment:failed', {
          flow_id: this.flowContext.flowId || '',
          payment_service: 'stripe',
          error_message: error.message,
        })
      }

      window.Savvy.addEventLogs?.([
        {
          flow_id: this.flowContext.flowId || '',
          event_type: 'payment:failed',
          timestamp: Date.now(),
          user_data_snapshot: this.userDataValue as UserData,
          data: {
            payment_service: 'stripe',
            error_message: error.message,
            component_id: this.component.id,
            component_key: this.component.key,
            component_path: ['pages', `${this.pageIndex}`, 'components', `${this.componentIndex}`],
          },
        },
      ])
    }
  }

  async confirmStripeIntent(
    token:
      | {
          card: stripe.elements.Element | { token: string }
          billing_details?: stripe.BillingDetails
          id?: string
        }
      | string,
    setPaymentMethod: boolean,
    billingDetails?: stripe.BillingDetails
  ) {
    const { component } = this.flowContext
    if (
      window.location.protocol !== 'https:' &&
      this.flowContext.component.skip_https_check !== true
    ) {
      this.setStatus('ready')
      return
    }
    log('StripeCheckout: component.enforce_validation', component.enforce_validation)
    log('StripeCheckout: this.isValidValue', this.isValidValue)
    if (component.enforce_validation && !this.isValidValue) {
      this.dispatch('validate')
      this.setStatus('ready')
      return
    }
    const publishableKey = getPKey(component.publishable_key)
    const stripe = this.stripePackage || Stripe(publishableKey)
    this.stripePackage = stripe
    try {
      const clientSecret = this.clientSecret || (await this.getClientSecret(billingDetails))
      if (!clientSecret) {
        this.setStatus('ready')
        return
      }
      const TokenId = typeof token === 'string' ? token : token.id
      const data = {
        payment_method:
          setPaymentMethod === true
            ? token
            : {
                card: { token: TokenId || '' },
                billing_details: this.computeContactInfo(billingDetails),
              },
      }

      const result =
        this.collectDetailsOnly || this.isSetupIntent
          ? await stripe.confirmCardSetup(clientSecret, data)
          : await stripe.confirmCardPayment(clientSecret, data)

      if (result.error) {
        // Show error to your customer (e.g., insufficient funds)
        this.dispatch('update', { detail: { key: component.key, value: 'failed' } })

        const actions = component.on_payment_failed_outputs
        if (Array.isArray(actions))
          this.dispatch('actions', {
            detail: { actions /*, context: { error: result.error }*/, component: this.component },
          })

        console.error(result.error.message)
        this.notifyOfError({ error: result.error })
        return result
      } else {
        // The payment has been processed!
        const intent = result['paymentIntent'] || result['setupIntent']
        if (this.collectDetailsOnly || (intent && intent.status === 'succeeded')) {
          try {
            const addPaymentMethodToUserData = Boolean(
              component.output_payment_method && component.payment_method_key
            )
            const paymentMethod =
              result && result['setupIntent'] && result['setupIntent'].payment_method
            if (this.collectDetailsOnly && addPaymentMethodToUserData && paymentMethod) {
              this.dispatch('update', {
                detail: { key: component.payment_method_key, value: paymentMethod },
              })
            }
          } catch (error) {
            console.error(error)
          }
          this.updateOnSuccess()
        } else this.dispatch('update', { detail: { key: component.key, value: 'no_error' } })

        return result
      }
    } catch (error) {
      const errmsg: { message: string; code: string; type: string } = {
        message:
          (error && error.response && error.response.data) ||
          error.message ||
          'Something went wrong internally - please refresh and try again. If the problem persists, let us know!',
        code: '',
        type: '',
      }

      this.dispatch('update', { detail: { key: component.key, value: 'failed' } })

      const actions = component.on_payment_failed_outputs
      if (Array.isArray(actions))
        this.dispatch('actions', {
          detail: { actions /*, context: { error: errmsg }*/, component: this.component },
        })

      if (error.response && error.response.data) console.error(error.response.data)
      else console.error(error)

      this.notifyOfError({ error: errmsg }, true)
    }
  }

  async getClientSecret(billingDetails?: stripe.BillingDetails) {
    const { flowId, component, componentId, flowVersion } = this.flowContext
    let details
    let amount
    let currency
    let price_id
    let one_time_price_id
    let promo_code
    let trial_period_days
    /* For now - all requests are test requests */
    const apidata = {
      flow_id: flowId,
      component_id: componentId,
      webapp: TEST_MODE,
      flow_version: flowVersion,
      details,
      amount,
      currency,
      price_id,
      one_time_price_id,
      promo_code,
      trial_period_days,
      enable_automatic_tax: component.enable_automatic_tax || false,
      place_hold_only: component.place_hold_only,
      allow_buy_now_pay_later: component.allow_buy_now_pay_later,
    }

    const shouldAddContactInfo =
      this.collectDetailsOnly ||
      component.action === 'subscription' ||
      (component.action === 'one-time-payment' && component.create_customer)

    if (shouldAddContactInfo) {
      apidata.details = this.computeContactInfo(billingDetails)
    }
    const shouldAddPaymentDetails = !(
      this.collectDetailsOnly || component.action === 'subscription'
    )
    if (shouldAddPaymentDetails) {
      const amount =
        (typeof component.amount === 'number' &&
          (parseInt(String(component.amount)) === component.amount
            ? parseInt(String(component.amount))
            : component.amount.toFixed(2))) ||
        0
      apidata.amount = +amount
      apidata.currency = component.currency
    }

    if (component.action === 'subscription') {
      const computeArray = (arr) =>
        Array.isArray(arr)
          ? arr.map((v) => computedValues(v, this.userDataValue))
          : computedValues(arr, this.userDataValue)
      apidata.price_id = computeArray(component.price_id)
      if (component.promo_code) apidata.promo_code = computeArray(component.promo_code)
      if (component.one_time_price_id)
        apidata.one_time_price_id = computeArray(component.one_time_price_id)
      if (component.trial_period_days)
        apidata.trial_period_days = computedValues(component.trial_period_days, this.userDataValue)
      apidata.currency = component.currency
      if (component.place_hold_only) apidata.place_hold_only = true
      if (component.allow_buy_now_pay_later) apidata.allow_buy_now_pay_later = true
    }

    try {
      const data = await callPaymentsBackend(Boolean(this.collectDetailsOnly), component, apidata)
      if (!data.success) {
        this.setErrorMessage(data.errorMessage)
        return null
      }

      if (component.output_customer_id && component.customer_id_key && data.customer_id) {
        this.dispatch('update', {
          detail: { key: component.customer_id_key, value: data.customer_id },
        })
      }
      //TODO subscription_id is deprecated - We should remove when we have a proper QA process (https://docs.embeddables.com/features/stripe-payments#collect-stripe-ids-to-send-to-your-backend)
      if (
        component.output_subscription_id &&
        component.subscription_id_key &&
        data.subscription_id
      ) {
        this.dispatch('update', {
          detail: { key: component.subscription_id_key, value: data.subscription_id },
        })
      }
      if (component.output_invoice_id && component.invoice_id_key && data.invoice_id) {
        this.dispatch('update', {
          detail: { key: component.invoice_id_key, value: data.invoice_id },
        })
      }
      if (
        component.output_payment_intent_id &&
        component.payment_intent_id_key &&
        data.payment_intent_id
      ) {
        this.dispatch('update', {
          detail: { key: component.payment_intent_id_key, value: data.payment_intent_id },
        })
      }
      const clientSecret = data.intent_client_secret
      this.clientSecret = clientSecret
      this.isSetupIntent = data.is_setup_intent || false
      return clientSecret
    } catch (error) {
      console.error(error)

      if (error.response && error.response.data) {
        this.setErrorMessage(error.response.data.errorMessage || error.response.data)
      } else {
        this.setErrorMessage(
          'Something went wrong internally - please refresh and try again. If the problem persists, let us know!',
          true
        )
      }
    }
  }

  computeContactInfo(walletBillingDetails?: stripe.BillingDetails): stripe.BillingDetails {
    const component = this.flowContext.component
    const name =
      computedValues(component.billing_info_name || '', this.userDataValue) ||
      walletBillingDetails?.name ||
      undefined
    const email =
      computedValues(component.billing_info_email || '', this.userDataValue) ||
      walletBillingDetails?.email ||
      undefined
    const phone =
      computedValues(component.billing_info_phone || '', this.userDataValue) ||
      walletBillingDetails?.phone ||
      undefined
    const addressLine1 = computedValues(
      component.billing_info_address_line1 || '',
      this.userDataValue
    )
    const addressLine2 = computedValues(
      component.billing_info_address_line2 || '',
      this.userDataValue
    )
    const city = computedValues(component.billing_info_city || '', this.userDataValue)
    const state = computedValues(component.billing_info_state || '', this.userDataValue)
    const country =
      computedValues(component.billing_info_country || '', this.userDataValue) || undefined
    const postal_code =
      computedValues(component.billing_info_postal_code || '', this.userDataValue) || undefined

    const address =
      ((country ||
        postal_code ||
        component.billing_info_address_line1 ||
        component.billing_info_address_line2 ||
        component.billing_info_city ||
        component.billing_info_state) && {
        line1: addressLine1,
        line2: addressLine2,
        city,
        state,
        country,
        postal_code,
      }) ||
      walletBillingDetails?.address ||
      undefined

    const contactInfo: stripe.BillingDetails = { name, email, phone, address }
    return contactInfo
  }

  setStatus(status: string) {
    this.localStatus = status
    switch (status) {
      /* Disable all buttons */
      case 'pending': {
        if (this.hasButtonTarget) this.buttonTarget.setAttribute('disabled', 'true')
        break
      }
      case 'success': {
        if (this.hasButtonTarget) this.buttonTarget.setAttribute('disabled', 'true')

        const component = this.flowContext.component
        let currencySymbol
        switch (component.currency) {
          case 'gbp': {
            currencySymbol = '£'
            break
          }
          case 'eur': {
            currencySymbol = '€'
            break
          }
          case 'usd':
          case 'cad':
          default: {
            currencySymbol = '$'
            break
          }
        }

        if (this.hasSuccessContainerTarget) {
          const amount =
            (typeof component.amount === 'number' &&
              (parseInt(String(component.amount)) === component.amount
                ? parseInt(String(component.amount))
                : component.amount.toFixed(2))) ||
            0
          const text = this.collectDetailsOnly
            ? `Your information has been saved!`
            : `Your payment of ${currencySymbol}${amount} has been received!`

          ;(this.successContainerTarget as HTMLElement).style.display = 'null'
          this.successContainerTarget.innerText = text
        }
        break
      }
      /* Enable all buttons */
      case 'ready': {
        if (this.hasButtonTarget) this.buttonTarget.removeAttribute('disabled')
        break
      }
      default:
        break
    }
  }

  setErrorMessage(message: string, blocking?: boolean) {
    if (blocking) this.errorIsBlocking = true
    this.errorMessage = message
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget?.querySelectorAll('div.message').forEach((el) => el.remove())
      if (message) {
        const messageEl = document.createElement('div')
        messageEl.classList.add('message')
        messageEl.innerText = message
        this.errorContainerTarget?.appendChild(messageEl)
        ;(this.errorContainerTarget as HTMLElement).style.display = ''
      } else (this.errorContainerTarget as HTMLElement).style.display = 'none'
    }
  }

  notifyOfError(
    err: {
      error: {
        message?: string
        code?: string
        type?: string
      }
      message?: string
    },
    skipSet?: boolean | undefined,
    blocking?: boolean | undefined
  ) {
    console.error(err)
    if (!skipSet) this.setErrorMessage(err.error ? err.error.message || '' : '', blocking)
    this.setStatus('ready')
    const errorType = err.error?.type
    if (errorType === 'validation_error' || errorType === 'card_error') return
    const slackMessage = `Stripe Error\n\ncode: ${(err.error && err.error.code) || 'n/a'}\ntype: ${
      errorType || err.message || JSON.stringify(err)
    }\nmessage: ${this.errorMessage || err.message || JSON.stringify(err)}\nerror:${JSON.stringify(
      err.error
    )}`
    const { message, code, type } = err.error
    const errObj = { message: message || '', code: code || '', type: type || '' }
    notify(slackMessage, 'C02QC92CGM8', undefined, errObj, this, window)
  }

  updateOnSuccess() {
    const component = this.flowContext.component

    this.setStatus('success')
    this.setErrorMessage('')
    this.clientSecret = null

    const successStatus = this.collectDetailsOnly || this.isSetupIntent ? CARD_ADDED : PAID
    this.dispatch('update', { detail: { key: component.key, value: successStatus } })

    log('StripeCheckout: component.next_on_complete', component.next_on_complete)

    if (component.next_on_complete) {
      log('StripeCheckout: this.flowContext.pageId', this.flowContext.pageId)
      this.dispatch('next', { detail: { value: this.flowContext.pageId } })
    }

    if (!doNotStoreComponentDataInCloud(this.component)) {
      jitsu.track('payment:completed', {
        flow_id: this.flowContext.flowId || '',
        payment_service: 'stripe',
      })
    }

    window.Savvy.addEventLogs?.([
      {
        flow_id: this.flowContext.flowId || '',
        event_type: 'payment:completed',
        timestamp: Date.now(),
        user_data_snapshot: this.userDataValue as UserData,
        data: {
          payment_service: 'stripe',
          component_id: this.component.id,
          component_key: this.component.key,
          component_path: ['pages', `${this.pageIndex}`, 'components', `${this.componentIndex}`],
        },
      },
    ])

    const actions = component.on_payment_complete_outputs
    if (Array.isArray(actions))
      this.dispatch('actions', { detail: { actions, component: this.component } })

    if (
      component.on_payment_complete_conversions &&
      Array.isArray(component.on_payment_complete_conversions)
    ) {
      this.dispatch('actions', {
        detail: {
          actions: component.on_payment_complete_conversions.map((c) => ({
            ...c,
            output: 'conversion',
          })),
          component: this.component,
        },
      })
    }
  }

  getActionStatus() {
    const key = this.flowContext.component.key
    const currentUserDataValue = this.userDataValue[key]

    const status = this.collectDetailsOnly
      ? currentUserDataValue === CARD_ADDED
      : currentUserDataValue === PAID
    const localStatus = this.localStatus === 'pending' || this.localStatus === 'success'
    const actionCompletedOrInProgress = status || localStatus
    return actionCompletedOrInProgress
  }

  updateCompleteStatus(key: string, value?: boolean) {
    /* NYI - Update the UI with the values based off cardComplete, cardCvcComplete, cardExpiryComplete, or cardComplete */
    if (!this.hasButtonTarget) return
    switch (key) {
      case 'cardComplete': {
        if (value !== undefined) this[key] = value
        value
          ? this.buttonTarget.classList.add(this.completeClass)
          : this.buttonTarget.classList.remove(this.completeClass)

        break
      }

      case 'cardExpiryComplete':
      case 'cardCvcComplete':
      case 'cardNumComplete': {
        if (value !== undefined) this[key] = value
        const isPotentiallyComplete =
          this.cardExpiryComplete && this.cardCvcComplete && this.cardNumComplete

        isPotentiallyComplete
          ? this.buttonTarget.classList.add(this.completeClass)
          : this.buttonTarget.classList.remove(this.completeClass)

        break
      }

      default:
        break
    }
  }

  setUpCard() {
    if (!this.hasCardTarget) return
    const { component } = this.flowContext
    const stripe = this.stripePackage || Stripe(getPKey(component.publishable_key))
    this.stripePackage = stripe
    const elements = stripe.elements()
    const style = { base: { padding: '12px' } }

    if (this.hasCardNumTarget && this.hasCardExpiryTarget && this.hasCardCvcTarget) {
      const cardNumberContainer = this.cardNumTarget
      const cardExpiryContainer = this.cardExpiryTarget
      const cardCvcContainer = this.cardCvcTarget
      // @ts-ignore
      const cardNumber = elements.create('cardNumber', { style })
      cardNumber.on('change', (e) => {
        // if (e.error) {
        //   this.buttonTarget.disabled = true
        //   this.setErrorMessage(
        //     e?.error?.message || e.error ? JSON.stringify(e.error) : 'An error occurred.'
        //   )
        // } else {
        this.updateCompleteStatus('cardNumComplete', e?.complete)
        //   const isPotentiallyComplete =
        //     this.cardExpiryComplete && this.cardCvcComplete && this.cardNumComplete
        //   this.buttonTarget.disabled = !isPotentiallyComplete
        // }
      })
      // @ts-ignore
      const cardCvc = elements.create('cardCvc', { style })
      cardCvc.on('change', (e) => {
        // if (e.error) {
        //   this.buttonTarget.disabled = true
        //   this.setErrorMessage(
        //     e?.error?.message || e.error ? JSON.stringify(e.error) : 'An error occurred.'
        //   )
        // } else {
        this.updateCompleteStatus('cardCvcComplete', e?.complete)
        //   const isPotentiallyComplete =
        //     this.cardExpiryComplete && this.cardCvcComplete && this.cardNumComplete
        //   this.buttonTarget.disabled = !isPotentiallyComplete
        // }
      })
      // @ts-ignore
      const cardExpiry = elements.create('cardExpiry', { style })
      cardExpiry.on('change', (e) => {
        // if (e.error) {
        //   this.setErrorMessage(
        //     e?.error?.message || e.error ? JSON.stringify(e.error) : 'An error occurred.'
        //   )
        // } else {
        this.updateCompleteStatus('cardExpiryComplete', e?.complete)
        //   const isPotentiallyComplete =
        //     this.cardExpiryComplete && this.cardCvcComplete && this.cardNumComplete
        //   this.buttonTarget.disabled = !isPotentiallyComplete
        // }
      })

      cardNumber.mount(cardNumberContainer)
      cardExpiry.mount(cardExpiryContainer)
      cardCvc.mount(cardCvcContainer)

      if (component.cc_field_logos_image) {
        const logosImg = document.createElement('img')
        logosImg.id = `${this.cardTarget.id}-logos-image`
        logosImg.src =
          replaceStorageURLsFromFirebaseToCloudflare(component.cc_field_logos_image) || ''
        logosImg.setAttribute(
          'style',
          'position: absolute; right: 16px; height: 22px; top: 0; bottom: 0; margin: auto;'
        )
        cardNumberContainer.appendChild(logosImg)
      }

      this.cardElement = cardNumber
    } else {
      // @ts-ignore
      const card = elements.create('card', { style })
      card.on('change', (e) => {
        if (e?.error) {
          this.buttonTarget.setAttribute('disabled', 'true')
          this.setErrorMessage(
            e?.error?.message || (e?.error ? JSON.stringify(e.error) : 'An error occurred.')
          )
        } else {
          this.setErrorMessage('')
          this.buttonTarget.removeAttribute('disabled')
          this.updateCompleteStatus('cardComplete', e?.complete)
        }
      })

      card.mount(this.cardTarget)
      this.cardElement = card
    }
  }

  async setUpPaymentElement() {
    if (!this.hasCardTarget) return
    const { component } = this.flowContext
    const stripe = this.stripePackage || Stripe(getPKey(component.publishable_key))
    this.stripePackage = stripe
    const clientSecret: string | undefined = this.clientSecret || (await this.getClientSecret())
    if (!clientSecret) return

    const elements = (stripe as any).elements({
      clientSecret,
      appearance: {
        theme: 'stripe',
      },
    })
    const paymentElement = elements.create('payment' as any)
    paymentElement.mount(this.cardTarget)
    this.cardElement = paymentElement
    this.cardTarget.style.display = 'block'

    if (this.hasButtonTarget) {
      this.buttonTarget.addEventListener('click', async (e) => {
        e.preventDefault()

        if (this.getActionStatus()) return
        if (this.isNotHTTPS) return

        this.setStatus('pending')
        this.setErrorMessage('')

        const { error } = await (stripe as any).confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: this.computeContactInfo(),
            },
          },
          redirect: 'if_required',
        })

        if (error) {
          this.notifyOfError({ error })
        } else {
          this.updateOnSuccess()
        }
      })
    }
  }

  async setUpWallet() {
    if (!this.hasWalletTarget) return
    const { component } = this.flowContext
    const stripe = this.stripePackage || Stripe(getPKey(component.publishable_key))
    this.stripePackage = stripe
    const elements = stripe.elements()
    const style = { base: { padding: '12px' } }

    const amount =
      (typeof component.amount === 'number' &&
        (parseInt(String(component.amount)) === component.amount
          ? parseInt(String(component.amount))
          : component.amount.toFixed(2))) ||
      0

    const paymentRequest = stripe.paymentRequest({
      country: component.country || '',
      currency: component.currency || 'usd',
      total: { label: component.label || 'Pay with Wallet', amount: Number(amount) * 100 },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    })

    paymentRequest.on('paymentmethod', async (ev) => {
      if (this.getActionStatus()) return
      const billingDetails = ev.paymentMethod.billing_details
      const result = await this.confirmStripeIntent(ev.paymentMethod.id, true, billingDetails)
      if (!result || result.error) {
        ev.complete('fail')
        result?.error ? this.notifyOfError({ error: result.error }) : ''
      } else ev.complete('success')
    })

    const canMakePayment = await paymentRequest.canMakePayment()
    if (canMakePayment) {
      // @ts-ignore
      const wallet = elements.create('paymentRequestButton', { paymentRequest, style })
      wallet.mount(this.walletTarget)
      this.walletElement = wallet
    } else {
      console.warn('Wallet Payments not supported')
    }
  }
}

function getPKey(pkey: string) {
  return TEST_MODE ? STRIPE_TEST_PKEY : pkey
}

async function callPaymentsBackend(
  collectDetailsOnly: boolean,
  component: StripeCheckout,
  apidata: StripeApiData
) {
  if (component.use_new_backend) {
    return callPaymentsBackendRenderer(collectDetailsOnly, component, apidata)
  } else {
    return callOldPaymentsBackend(collectDetailsOnly, component, apidata)
  }
}

async function callPaymentsBackendRenderer(
  collectDetailsOnly: boolean,
  component: StripeCheckout,
  apidata: StripeApiData
) {
  const path = collectDetailsOnly
    ? `/payments/set-up-payment-intent`
    : component.action === 'subscription'
    ? `/payments/set-up-subscription`
    : `/payments/create-payment`
  const url = `${getRendererBase()}${path}`

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(apidata),
    headers: {
      'Content-Type': 'application/json',
      // Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImllcnhleGR0eWFzaHVvdGNzanlvIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NjQ4ODU2NDIsImV4cCI6MTk4MDQ2MTY0Mn0.AbUQz18lps79WZBW1kmbFP2lUTiAF6ttTuFK14yvUuw`,
    },
  })
  const data = await response.json()
  return data
}

async function callOldPaymentsBackend(
  collectDetailsOnly: boolean,
  component: StripeCheckout,
  apidata: StripeApiData
) {
  const oldApiData: OldStripeApiData = {
    ...apidata,
    webapp: TEST_MODE,
    formId: apidata.flow_id,
    componentId: apidata.component_id,
    flowVersion: apidata.flow_version,
  }
  const url = collectDetailsOnly
    ? `/public/billing/flow-setup-intent`
    : component.action === 'subscription'
    ? `/public/billing/set-up-subscription`
    : `/public/billing/create-payment-intent`

  const { data } = await api(url, oldApiData)
  return data
}
